import type { CopilotBlockingReading } from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { pogToStudioItems } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import { measureBlockingOf } from "src/pages/CdtStudio/domain/blockOrientation";
import type { StudioItem } from "src/pages/CdtStudio/domain/studioTypes";

const LEVEL_NAMES: Record<string, string> = {
  variant: "the top-level group",
  cdt1: "CDT 1",
  cdt2: "CDT 2",
  cdt3: "CDT 3",
};

const CDT_COLUMNS = ["variant", "cdt1", "cdt2", "cdt3"] as const;

const NO_BLOCK = /^(unspecified|unassigned\b.*|others?|n\/?a|none|unknown|—|-)?$/i;

const blockValue = (item: StudioItem, attr: string): string | null => {
  const value = item.attrs[attr]?.trim() ?? "";
  return NO_BLOCK.test(value) ? null : value;
};

const topLevelOf = (items: readonly StudioItem[]): string | undefined =>
  CDT_COLUMNS.find((attr) => {
    const values = items.map((item) => blockValue(item, attr));
    const carried = values.filter((value) => value !== null).length;
    const named = new Set(values.filter(Boolean)).size;
    const blocks = new Set(values.map((value) => value ?? "—")).size;
    return carried >= items.length / 2 && named >= 2 && blocks <= items.length / 2;
  });

export const basePogBlockingOf = (pog: Pog | null): CopilotBlockingReading | null => {
  if (!pog) return null;
  try {
    const { items, runs } = pogToStudioItems([pog]);
    const attr = topLevelOf(items);
    if (!attr || items.length === 0) return null;
    const reading = measureBlockingOf(items, attr, runs);
    if (!Number.isFinite(reading.score)) return null;
    return {
      role: reading.role,
      attribute: LEVEL_NAMES[attr] ?? attr,
      score: Math.min(1, Math.max(0, Math.round(reading.score * 100) / 100)),
    };
  } catch {
    return null;
  }
};
